/**
 * Login Page - Admin authentication
 * Version: 6.2.0
 * 
 * Changes in v6.2.0:
 * - Added Remember Me checkbox functionality
 * - Fixed Forgot Password link to navigate to Register page with Forgot tab pre-selected
 */
import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import apiClient from '../api/apiClient'
import './Login.css'

interface LoginResponse {
  access_token: string
  refresh_token: string
  token_type: string
  expires_in: number
  user_id: string
  username: string
  role: string
}

function Login() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Check for stored email if Remember Me was previously checked
  useEffect(() => {
    const storedEmail = localStorage.getItem('remembered_email')
    if (storedEmail) {
      setEmail(storedEmail)
      setRememberMe(true)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    console.log('[Login] Submitting with:', { email, password })

    try {
      console.log('[Login] Making API call to /api/auth/login')
      const response = await apiClient.post<LoginResponse>('/api/auth/login', {
        username: email,
        password: password,
      })
      console.log('[Login] API Response:', response.data)

      // Handle Remember Me functionality
      if (rememberMe) {
        localStorage.setItem('remembered_email', email)
      } else {
        localStorage.removeItem('remembered_email')
      }

      // Store tokens in localStorage
      localStorage.setItem('auth_token', response.data.access_token)
      localStorage.setItem('refresh_token', response.data.refresh_token)
      localStorage.setItem('user_id', response.data.user_id)
      localStorage.setItem('username', response.data.username)
      localStorage.setItem('user_role', response.data.role)

      // Check for redirect after login
      const redirectPath = localStorage.getItem('redirectAfterLogin')
      console.log('[Login] Redirect path:', redirectPath)
      if (redirectPath) {
        localStorage.removeItem('redirectAfterLogin')
        console.log('[Login] Navigating to:', redirectPath)
        navigate(redirectPath)
      } else {
        console.log('[Login] Navigating to: /')
        navigate('/')
      }
    } catch (err: any) {
      console.error('[Login] Error:', err)
      if (err.response?.data?.detail) {
        setError(err.response.data.detail)
      } else if (err.message) {
        setError('Connection error. Please check if the server is running.')
      } else {
        setError('Login failed. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-page">
      <div className="login-container">
        <div className="login-header">
          <div className="logo-section">
            <span className="logo-icon">🐂</span>
            <h1>ZO Bull</h1>
          </div>
          <p className="tagline">ZO Bull Algo Trading Platform</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              disabled={loading}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              Password
              <Link to="/register?mode=forgot" className="forgot-link">Forgot?</Link>
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              disabled={loading}
              required
            />
          </div>

          <div className="form-group checkbox-group remember-me">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                disabled={loading}
              />
              <span>Remember me</span>
            </label>
          </div>

          <button type="submit" className="btn btn-primary login-btn" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>

          <div className="form-divider">
            <span>or</span>
          </div>

          <p className="switch-mode">
            Don't have an account?{' '}
            <Link to="/register">Sign Up Free</Link>
          </p>
        </form>

        <div className="login-footer">
          <div className="footer-links">
            <Link to="/landing">← Back to Home</Link>
            <span className="dot">•</span>
            <Link to="/terms">Terms</Link>
            <span className="dot">•</span>
            <Link to="/privacy">Privacy</Link>
          </div>
          <p>ZO Bull v6.2.0 | © 2024-2026</p>
        </div>
      </div>
    </div>
  )
}

export default Login